/**
 * GSTR-3B — the statutory form, as the legacy renders it (ADR-0021).
 *
 * The legacy lays 3B out as **Form GSTR-3B, [See rule 61(5)]** with its real
 * section numbering, and a practitioner reads it the way they read the paper
 * return. A flat list of cell ids is a debugging view; this is the form.
 *
 * **Read-only, bar four cells, and that is the design.** The service derives every
 * cell (W2-05: the 88A convention, carry-forward, the 4B floor); a screen that let
 * someone type into 3B would be deciding a tax liability, which ADR-0014 forbids.
 * The exceptions are the ones V-d hands to a person (C-03): 4(B)(1) and 4(B)(2),
 * and the additions to 4(D) — each typed with a note, which prints beside the
 * figure. The legacy agrees — its 3B is read-only bar one ITC bucket control.
 *
 * **Every cell of the form is here (C-03, FR-E18)**, each a figure ProGST
 * computed, a figure a person typed, or a state in words (`lib/gstr3bForm.ts`).
 *
 * **The GSTR-3B period's screen** (FR-E17, e2e-filing C-02). The rail lists the
 * client's GSTR-3B periods — not the GSTR-1's, which this screen read until C-02,
 * so the 3B had no state of its own here — and offers to open the 3B for a month
 * whose GSTR-1 exists (ND-E10, on request). The lifecycle strip is the one Returns
 * uses; the gates panel is the 3B's; 2(d) is the day the filing was recorded.
 *
 * **A period screen, top to bottom (C-14):** the lifecycle strip; the period's state; filing on
 * the GST portal (the panel from approval on, a sentence before); the gates; the files; the
 * form; 6.1 with the ledger balances typed beside it; the challan, and PMT-06 for a quarter;
 * the GSTR-3B JSON; the tie-out. A quarter is written in words wherever a period is printed.
 *
 * **A gated cell renders as its state, never as ₹0.00.** `pending_verification`,
 * `not_computed`, `decided_outside` and `declined` are not zero: zero is a
 * claim that the value is nil, and
 * on 3.1(b) — zero-rated supplies — that claim is the difference between an
 * exporter's refund and a wrong return. The legacy shows a blank or a dash and
 * lets the operator fill it; we name *why* it is dark and what it waits on.
 */

import { Alert, Button, Card } from '@fiveox/ui'
import { FileSpreadsheet, Table2 } from 'lucide-react'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'

import { PERIOD, useEvaluation } from '../lib/access'
import {
  ApiError,
  type Gstr3bBody,
  type PeriodGates,
  type PeriodRecord,
  periods,
} from '../lib/api'
import {
  type Cell,
  ECO_ROWS,
  type FormRow,
  INTEREST_ROWS,
  INTER_STATE_ROWS,
  INWARD_EXEMPT_ROWS,
  ITC_ROWS,
  OUTWARD_ROWS,
  kindOf,
  listedInputs,
  saysOf,
  stateOf,
  stateWords,
  typedNote,
  verifiedWords,
} from '../lib/gstr3bForm'
import { carriedForward } from '../lib/carriedForward'
import {
  MINOR_HEADS,
  challanLeftOut,
  challanRows,
  generateChallanWorking,
  jsonHolds,
} from '../lib/challan'
import { awaitingWords } from '../lib/invoicePreview'
import { clientPath } from '../lib/clientScope'
import { saveFile } from '../lib/files'
import { filedOn, monthsWithoutA3b } from '../lib/lifecycle'
import { paymentOf } from '../lib/payment'
import { perHeadNet, perHeadNetWhole } from '../lib/perHeadNet'
import { isQuarter, periodWords } from '../lib/periodWords'
import { STATE_LABELS as PERIOD_STATE_LABELS, artifactWords } from '../lib/returnWords'
import { reverseChargeLines } from '../lib/reverseCharge'
import { unmatchedCreditNotes } from '../lib/unmatchedCreditNotes'
import { type GeneratedPaper, generateWorkingPaper } from '../lib/workingPaper'
import { useClient } from './ClientLayout'
import { Gstr3bAutocalc } from './Gstr3bAutocalc'
import { Gstr3bLedgers } from './Gstr3bLedgers'
import { Gstr3bTypedFigure } from './Gstr3bTypedFigure'
import { GateList } from './GateList'
import { LifecycleActions } from './LifecycleActions'
import { FILING_STATES, PeriodFilingPanel } from './PeriodFilingPanel'
import { Pmt06Card } from './Pmt06Card'

/**
 * The form's *Year*: a month's calendar year as before; a quarter's financial year (FR-E16),
 * which is what its spelling carries (`Q2-2026-27` → `2026-27`).
 */
function yearOf(period: string | undefined): string {
  if (!period) return '—'
  return isQuarter(period) ? period.slice(3) : (period.split('-')[1] ?? '—')
}

const HEADS = ['igst', 'cgst', 'sgst', 'cess'] as const
type Head = (typeof HEADS)[number]

const HEAD_LABELS: Record<Head, string> = {
  igst: 'Integrated tax',
  cgst: 'Central tax',
  sgst: 'State/UT tax',
  cess: 'Cess',
}

function rupees(paise: unknown): string {
  if (typeof paise !== 'number') return '—'
  return (paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Shown under the row label only where the cell is dark — a state, not a figure. */
function gateOf(cell: Cell): string | null {
  return stateOf(cell) ? saysOf(cell) : null
}

function CellValue({ cell, head }: { cell: Cell; head: Head | 'taxable' }) {
  const state = stateOf(cell)
  if (state) {
    // Deliberately not a number. Rendering 0.00 here would assert nil.
    return <span className="gst-chip is-hold">{stateWords(state)}</span>
  }
  const value = cell?.[head]
  return <>{rupees(value)}</>
}

interface Column {
  key: string
  label: string
}

const HEAD_COLUMNS: Column[] = HEADS.map((h) => ({ key: h, label: HEAD_LABELS[h] }))

/**
 * One block of the form: its rows, each a figure, a typed figure with its note, or a state
 * in words. `alwaysSay` prints the service's sentence under every row, not only dark ones —
 * the rows of 4 and 5.1 do not expand, so their source is shown where it is read.
 */
function FormBlock({
  title,
  head,
  rows,
  cells,
  columns,
  alwaysSay = false,
}: {
  title?: string
  head: string
  rows: Array<FormRow & { indent?: boolean }>
  cells: Record<string, Cell>
  columns: Column[]
  alwaysSay?: boolean
}) {
  return (
    <>
      {title && <h3 className="gst-sub mb-2">{title}</h3>}
      <div className="gst-table-wrap mb-5">
        <table className="gst-table">
          <thead>
            <tr>
              <th className="is-text">{head}</th>
              {columns.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const cell = cells[row.id]
              const said = alwaysSay ? saysOf(cell) : gateOf(cell)
              const note = typedNote(cell)
              const unverified = verifiedWords(cell)
              return (
                <tr key={row.id} className={i % 2 === 1 ? 'is-a' : ''}>
                  <td
                    className="is-text is-sec"
                    style={row.indent ? { paddingLeft: '1.5rem' } : undefined}
                  >
                    {row.label}
                    {kindOf(cell) === 'typed' && (
                      <span className="gst-chip ml-2">Typed by a person</span>
                    )}
                    {unverified && <span className="gst-chip is-hold ml-2">{unverified}</span>}
                    {/* The 42/43 engine stamps a result unverified until a practitioner
                        signs the inputs off (the legacy `reversal=` path). */}
                    {row.id === '4B' && cell?.verified === false && (
                      <span className="gst-chip is-hold ml-2">unverified</span>
                    )}
                    {!cell && <small>Not in this computation.</small>}
                    {said && <small>{said}</small>}
                    {note && <small>Note: {note}</small>}
                  </td>
                  {columns.map((c) => (
                    <td key={c.key}>
                      {stateOf(cell) ? (
                        // Deliberately not a number. Rendering 0.00 here would assert nil.
                        <span className="gst-chip is-hold">{stateWords(stateOf(cell) ?? '')}</span>
                      ) : (
                        rupees(cell?.[c.key])
                      )}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

/** Beside 4(B)(1): the Rule 42/43 inputs ProGST can list (V-d — listed, never computed). */
function ListedInputs({ cell }: { cell: Cell }) {
  const listed = listedInputs(cell)
  if (!listed) return null
  return (
    <div className="mb-5">
      <p className="gst-panel-h mb-1">For 4(B)(1): the inputs ProGST can list</p>
      <ul className="gst-panel-list">
        {listed.exemptTurnover !== null && (
          <li>
            <span className="gst-k">E</span>
            <span>Exempt, nil-rated and non-GST turnover: ₹{rupees(listed.exemptTurnover)}</span>
          </li>
        )}
        {listed.totalTurnover !== null && (
          <li>
            <span className="gst-k">F</span>
            <span>Total turnover: ₹{rupees(listed.totalTurnover)}</span>
          </li>
        )}
        {listed.inputTax && (
          <li>
            <span className="gst-k">T</span>
            <span>
              Input tax this month:{' '}
              {HEADS.map((h) => `${HEAD_LABELS[h]} ₹${rupees(listed.inputTax?.[h] ?? 0)}`).join(
                ', ',
              )}
            </span>
          </li>
        )}
        {listed.notCaptured && (
          <li>
            <span className="gst-k">T1–T4</span>
            <span>Credit by use is not captured: a purchase register does not say which purchases served exempt or non-business supplies.</span>
          </li>
        )}
      </ul>
    </div>
  )
}

export function Gstr3b() {
  const client = useClient()
  const [rows, setRows] = useState<PeriodRecord[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [body, setBody] = useState<Gstr3bBody | null>(null)
  /**
   * The 3B period's gates (W3-13 (3c), FR-E25): the purchase documents it waits on, the tie-out
   * against the month's GSTR-1, Table 12's blockers, and its files — the same read Returns
   * makes, so what blocks Prepare here and there is one computation.
   */
  const [gates, setGates] = useState<PeriodGates | null>(null)
  /** The period with its history — where the day it was recorded filed is (2(d)). */
  const [detail, setDetail] = useState<PeriodRecord | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const { can } = useEvaluation()
  const [generating, setGenerating] = useState(false)
  const [busy, setBusy] = useState(false)
  const [paper, setPaper] = useState<GeneratedPaper | null>(null)
  /** Bumped when the filing panel fetched GSTN's figures, so their card reads them again. */
  const [autocalcRead, setAutocalcRead] = useState(0)

  /** This client's GSTR-3B periods — the ones this screen is of. */
  const threeBs = rows.filter((r) => r.return_type === 'GSTR3B')
  const openable = monthsWithoutA3b(rows)

  const loadRows = useCallback(async () => {
    // Scoped to the client in the URL (ND-C1). Before this, every screen
    // listed every client's periods together and the rail asked you to pick
    // one out of the pile — which is the shape of a wrong-client mistake.
    try {
      const b = await periods.list({ client_id: client.id })
      setRows(b.data)
      // NOT `cur ?? …`: the effect re-runs when the client changes, and
      // keeping the previous selection would leave another client's period
      // on screen. Right numbers, wrong client is the mistake this whole
      // slice exists to make impossible. And a GSTR3B row, never the
      // month's GSTR-1: this is the 3B period's screen (C-02).
      setSelected((cur) => {
        const mine = b.data.filter((r) => r.return_type === 'GSTR3B')
        return mine.find((r) => r.id === cur)?.id ?? mine[0]?.id ?? null
      })
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Returns could not be loaded.')
    }
  }, [client.id])

  useEffect(() => {
    setSelected(null)
    void loadRows()
  }, [loadRows])

  const load = useCallback(async () => {
    if (!selected) {
      setBody(null)
      setGates(null)
      setDetail(null)
      return
    }
    try {
      setBody((await periods.gstr3b(selected)).data)
      setPaper(null)
      setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The 3B could not be derived.')
    }
    // Two more reads, and a failed one is simply not shown — the 3B above stands on its own.
    try {
      setGates((await periods.gates(selected)).data)
    } catch {
      setGates(null)
    }
    try {
      setDetail((await periods.get(selected)).data)
    } catch {
      setDetail(null)
    }
  }, [selected])

  useEffect(() => {
    void load()
  }, [load])

  /** One lifecycle call, then the rail and the period again; a refusal's sentence shown. */
  const act = useCallback(
    async (fn: () => Promise<unknown>) => {
      setBusy(true)
      setError(null)
      try {
        await fn()
        await Promise.all([loadRows(), load()])
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That action failed.')
      } finally {
        setBusy(false)
      }
    },
    [loadRows, load],
  )

  const waiting = gates?.awaiting_approval ?? []
  const blockers =
    (gates?.blocked_table12.length ?? 0) + (gates?.tieout.length ?? 0) + waiting.length

  /**
   * FR-46 (C-09): the form prints the figures AS FILED — the service's whole-rupee view, the
   * numbers the GSTR-3B JSON and the return sheet carry — not the paise they were derived in.
   * A cell's state and its sentence are the same in both; only a figure is rounded, once, there.
   */
  const view = body?.return_view
  const cells = (view?.cells ?? body?.gstr3b?.cells ?? {}) as Record<string, Cell>
  const setoff = (view?.setoff ?? body?.gstr3b?.setoff) as
    | {
        cash?: Record<string, number>
        utilisation?: Record<string, Record<string, number>>
        credit_remaining?: Record<string, number>
      }
    | undefined
  const payment = paymentOf(cells['6.1'])
  // D-37 beside a whole-rupee 6.1: shown in whole rupees too, on this screen only (C-09), and
  // only once 6.1 is established (W3-18 (1)).
  const netView = payment.established
    ? perHeadNetWhole(perHeadNet(cells['6.1']), setoff?.cash)
    : null

  /**
   * Finding A3: the working paper (W3-08d, ADR-0031) had a route, a label on
   * Download, and no way to make one. Generated for the period on screen, then
   * downloaded straight away — the reason to press it is to read it — and it
   * stays on Download with the period's other artifacts.
   */
  /** C-08: the challan working, made for the period on screen and saved straight away. */
  const downloadChallan = async () => {
    if (!selected) return
    setBusy(true)
    try {
      const made = await generateChallanWorking(selected)
      setError(null)
      saveFile(made.file, 'GSTR-3B challan working.xlsx')
      await load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The challan working could not be made.')
    } finally {
      setBusy(false)
    }
  }

  /** C-09: the GSTR-3B JSON on demand; a hold comes back as the refusal naming the cell. */
  const makeJson = () =>
    act(async () => {
      if (selected) await periods.generateGstr3bJson(selected)
    })

  const generate = async () => {
    if (!selected) return
    setGenerating(true)
    try {
      const made = await generateWorkingPaper(selected)
      setPaper(made)
      setError(null)
      saveFile(made.file, 'GSTR-3B working paper.xlsx')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The working paper could not be generated.')
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="gst-h1">
            <Table2 size={20} /> Form GSTR-3B
          </h1>
          <p className="gst-sub">[See rule 61(5)] — derived from the same draft GSTR-1 reads</p>
        </div>
        {selected && can(PERIOD, 'can_update') && (
          <span className="flex flex-wrap items-center gap-2">
            {/*
              * The 3B period's lifecycle (FR-E17): the strip Returns shows, for this
              * period. Prepare runs the tie-out and makes the working paper; send for
              * approval binds the paper's hash; approval, filing and lock as for GSTR-1.
              */}
            {gates && gates.period.id === selected && (
              <LifecycleActions
                key={gates.period.id}
                period={gates.period}
                artifacts={gates.artifacts}
                busy={busy}
                act={act}
              />
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={generating || busy}
              onClick={() => void generate()}
            >
              <FileSpreadsheet size={14} />
              {generating ? 'Generating…' : 'Generate working paper'}
            </Button>
          </span>
        )}
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}
      {paper && (
        <Alert tone="success" className="mb-4">
          Working paper v{paper.artifact.version_no} generated and downloading. It is kept with
          this period's other files on{' '}
          <Link to={clientPath(client.id, 'download')}>Download</Link>.
        </Alert>
      )}

      <div className="flex flex-wrap gap-6">
        <aside className="w-64 shrink-0">
          {threeBs.length === 0 ? (
            <div className="gst-empty">
              {rows.length === 0 ? (
                <>
                  No periods yet. Open one on{' '}
                  <Link to={clientPath(client.id, 'returns')} className="gst-link">
                    Returns
                  </Link>
                  .
                </>
              ) : (
                'No GSTR-3B period yet.'
              )}
            </div>
          ) : (
            <ul className="space-y-1">
              {threeBs.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(row.id)}
                    aria-current={row.id === selected}
                    className="gst-rail-item"
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate">GSTR-3B</span>
                      <span className={`gst-state ${row.state === 'open' ? '' : 'is-go'}`}>
                        {PERIOD_STATE_LABELS[row.state] ?? row.state}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-[11px]">{periodWords(row.period)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {/*
            * ND-E10, on request: a month with a GSTR-1 and no GSTR-3B yet is offered its 3B
            * here, one button per month. The other answer — opened with the GSTR-1 — is the
            * service's one-line seam (`api/periods.py::open_period`), not a second button.
            */}
          {can(PERIOD, 'can_create') && openable.length > 0 && (
            <div className="mt-3 space-y-1">
              {openable.map((month) => (
                <Button
                  key={month}
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    void act(() =>
                      periods.open({ client_id: client.id, return_type: 'GSTR3B', period: month }),
                    )
                  }
                >
                  Open the GSTR-3B for {periodWords(month)}
                </Button>
              ))}
            </div>
          )}
        </aside>

        <section className="min-w-0 flex-1">
          {!body ? (
            <p className="gst-sub">
              {threeBs.length > 0
                ? 'Select a GSTR-3B period.'
                : openable.length > 0 && can(PERIOD, 'can_create')
                  ? 'Open the GSTR-3B for a period on the left; its form is shown here.'
                  : 'This client has no GSTR-3B period yet.'}
            </p>
          ) : (
            <>
              {/* -- the period: its state, its filing, its gates, its files -- */}
              {gates && gates.period.id === selected && (
                <>
                  <p className="gst-meta mb-3">
                    {periodWords(gates.period.period)} ·{' '}
                    {PERIOD_STATE_LABELS[gates.period.state] ?? gates.period.state}
                    {gates.period.filed_ack_no && ` · ACK ${gates.period.filed_ack_no}`}
                    {(gates.period.state === 'filed' || gates.period.state === 'locked') &&
                      ' · A filed GSTR-3B uploaded here is kept as the record of what was filed; its figures are not read yet.'}
                  </p>
                  {/*
                    * D-07..D-10: filing on the GST portal, inside the period it belongs to (it sat
                    * above the rail before C-14). From the client's approval on, the panel — the
                    * 3B's own strip (D-10): save, GSTN's figures beside ours (the card below
                    * re-reads them), offset, EVC OTP, file. Before that, the card is there and
                    * says when it opens, so the screen never leaves a person wondering where
                    * filing is.
                    */}
                  {FILING_STATES.has(gates.period.state) ? (
                    <PeriodFilingPanel
                      key={`filing:${gates.period.id}`}
                      periodId={gates.period.id}
                      clientId={client.id}
                      onChanged={() => {
                        setAutocalcRead((n) => n + 1)
                        void Promise.all([loadRows(), load()])
                      }}
                    />
                  ) : (
                    <Card className="mb-4 p-4" data-testid="period-filing">
                      <p className="gst-panel-h mb-2">Filing on the GST portal</p>
                      <p className="gst-meta">
                        Nothing to file yet: filing opens once the client has approved this
                        return.
                      </p>
                    </Card>
                  )}
                  <Alert tone={gates.blocks_prepared ? 'warning' : 'success'} className="mb-4">
                    <p className="gst-panel-h">
                      Gates
                      <span className={`gst-tag ${gates.blocks_prepared ? '' : 'is-clear'}`}>
                        {gates.blocks_prepared ? 'blocked · prepare refused' : 'clear'}
                      </span>
                    </p>
                    {blockers === 0 ? (
                      <p className="text-sm">
                        Nothing blocks this return. Its outward figures tie to the month's GSTR-1.
                      </p>
                    ) : (
                      <ul className="gst-panel-list">
                        {waiting.length > 0 && (
                          <li>
                            <span className="gst-k">Awaiting approval</span>
                            <span>
                              {awaitingWords(waiting)} Their input credit is not in this 3B until a
                              person approves them, and Prepare is refused.{' '}
                              <Link to={clientPath(client.id, 'intake')}>Approve on Upload Data</Link>.
                            </span>
                          </li>
                        )}
                        {gates.tieout.map((t, i) => (
                          <li key={`t${i}`}>
                            <span className="gst-k">Tie-out with GSTR-1</span>
                            <span>{String(t.message)}</span>
                          </li>
                        ))}
                        <GateList
                          key={`${gates.period.id}:Table 12`}
                          label="Table 12"
                          consequence="blocks prepared"
                          rows={gates.blocked_table12}
                        />
                      </ul>
                    )}
                  </Alert>
                  {gates.artifacts.length > 0 && (
                    <Card className="mb-4 p-3">
                      {gates.artifacts.map((a) => (
                        <p key={`${a.kind}v${a.version_no}`} className="gst-meta">
                          {artifactWords(a, gates.artifacts)}
                        </p>
                      ))}
                      <p className="gst-meta mt-1">
                        Download them on{' '}
                        <Link to={clientPath(client.id, 'download')} className="gst-link">
                          Download
                        </Link>
                        .
                      </p>
                    </Card>
                  )}
                </>
              )}
              {/* -- the header block the form carries ----------------------- */}
              <div className="gst-table-wrap mb-4">
                <table className="gst-table">
                  <tbody>
                    <tr>
                      <td className="is-text is-sec">Year</td>
                      <td className="is-text">{yearOf(body.gstr3b.period)}</td>
                      <td className="is-text is-sec">Period</td>
                      <td className="is-text">{periodWords(body.gstr3b.period ?? '')}</td>
                    </tr>
                    <tr className="is-a">
                      <td className="is-text is-sec">GSTIN of the supplier</td>
                      <td className="is-text" colSpan={3}>
                        <span className="gst-mono">{body.client_gstin ?? "—"}</span>
                      </td>
                    </tr>
                    <tr>
                      <td className="is-text is-sec">2(a). Legal name of the registered person</td>
                      <td className="is-text" colSpan={3}>
                        {body.client_name}
                      </td>
                    </tr>
                    {/* ARN is assigned by the portal on filing. Blank until then,
                        as on the form — not a value we can invent. 2(d) is the day
                        the practice recorded it here, from the period's own history
                        (`filedOn`); the portal's date is in the filed return, which
                        Wave E reads. */}
                    <tr className="is-a">
                      <td className="is-text is-sec">2(c). ARN</td>
                      <td className="is-text">{body.period.filed_ack_no ?? '—'}</td>
                      <td className="is-text is-sec">2(d). Date of ARN</td>
                      <td className="is-text">{filedOn(detail?.history) ?? '—'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="gst-meta mb-3">(Amount in ₹ for all tables)</p>

              {/* -- 3.1 ---------------------------------------------------- */}
              <h3 className="gst-sub mb-2">
                3.1 Details of Outward supplies and inward supplies liable to reverse charge
              </h3>
              <div className="gst-table-wrap mb-5">
                <table className="gst-table">
                  <thead>
                    <tr>
                      <th className="is-text">Nature of Supplies</th>
                      <th>Total taxable value</th>
                      {HEADS.map((h) => (
                        <th key={h}>{HEAD_LABELS[h]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {OUTWARD_ROWS.map((row, i) => {
                      const cell = cells[row.id]
                      const gate = gateOf(cell)
                      const expandable = !!cell && !stateOf(cell)
                      return (
                        <Fragment key={row.id}>
                          <tr
                            key={row.id}
                            className={i % 2 === 1 ? 'is-a' : ''}
                            onClick={() => expandable && setOpen(open === row.id ? null : row.id)}
                            style={expandable ? { cursor: 'pointer' } : undefined}
                          >
                            <td className="is-text is-sec">
                              {row.label}
                              {gate && <small>{gate}</small>}
                            </td>
                            <td>
                              <CellValue cell={cell} head="taxable" />
                            </td>
                            {HEADS.map((h) => (
                              <td key={h}>
                                <CellValue cell={cell} head={h} />
                              </td>
                            ))}
                          </tr>
                          {/* The legacy expands a row into where its number came
                              from. Ours shows the service's own provenance. */}
                          {open === row.id && cell?.source != null && (
                            <tr key={`${row.id}-src`}>
                              <td className="is-text" colSpan={6}>
                                <span className="gst-meta">
                                  Source: {saysOf(cell) ?? '—'}
                                </span>
                              </td>
                            </tr>
                          )}
                          {/* W3-18 (5), D-105: 3.1(d)'s three lines, always shown under it. */}
                          {row.id === '3.1(d)' &&
                            reverseChargeLines(cell).map((sub) => (
                              <tr key={`${row.id}-${sub.key}`}>
                                <td className="is-text">
                                  <span className="gst-meta">{sub.label}</span>
                                </td>
                                {sub.values ? (
                                  <>
                                    <td>{rupees(sub.values.taxable)}</td>
                                    {HEADS.map((h) => (
                                      <td key={h}>{rupees(sub.values?.[h])}</td>
                                    ))}
                                  </>
                                ) : (
                                  <td colSpan={5}>
                                    <span className="gst-chip is-hold">{sub.state}</span>
                                  </td>
                                )}
                              </tr>
                            ))}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* -- 3.1.1 -------------------------------------------------- */}
              <FormBlock
                title="3.1.1 Details of supplies notified under section 9(5) of the CGST Act"
                head="Nature of Supplies"
                rows={ECO_ROWS}
                cells={cells}
                columns={[{ key: 'taxable', label: 'Total taxable value' }, ...HEAD_COLUMNS]}
                alwaysSay
              />

              {/* -- 3.2 ---------------------------------------------------- */}
              <FormBlock
                title="3.2 Out of supplies made in 3.1(a) and 3.1.1(i), details of inter-state supplies"
                head="Nature of Supplies"
                rows={INTER_STATE_ROWS}
                cells={cells}
                columns={[
                  { key: 'taxable', label: 'Total taxable value' },
                  { key: 'igst', label: 'Integrated tax' },
                ]}
              />

              {/* -- 4 ------------------------------------------------------ */}
              <h3 className="gst-sub mb-2">4. Eligible ITC</h3>
              {/* D-31. These notes reduce nothing in 4(A) by the owner's ruling,
                  which is exactly why they are said here, beside the figures
                  they qualify, rather than left in a provenance line nobody
                  opens. */}
              {(() => {
                const flag = unmatchedCreditNotes(cells['4A'])
                if (!flag) return null
                return (
                  <Alert tone="warning" className="mb-3">
                    {flag.count} credit {flag.count === 1 ? 'note' : 'notes'} in
                    this period&apos;s 2B matched no purchase document, so{' '}
                    {flag.count === 1 ? 'it reduces' : 'they reduce'} nothing
                    below. The credit {flag.count === 1 ? 'it' : 'they'} would
                    reverse is still counted in 4(A)
                    {flag.amounts.length > 0 && (
                      <>
                        {' '}
                        —{' '}
                        {flag.amounts
                          .map((a) => `${HEAD_LABELS[a.head]} ${rupees(a.paise)}`)
                          .join(', ')}
                      </>
                    )}
                    . Match or suppress {flag.count === 1 ? 'it' : 'them'} in
                    Reconciliation.
                  </Alert>
                )
              })()}
              {/* D-38. Credit in 4(A) this month that was booked in an earlier
                  one — said beside the figure, because a practitioner comparing
                  this month's register with 4(A) would otherwise find more
                  credit than the month's purchases. */}
              {(() => {
                const carried = carriedForward(cells['4A'])
                if (!carried) return null
                return (
                  <Alert tone="info" className="mb-3">
                    {`4(A) includes credit on ${carried.count} ${
                      carried.count === 1 ? 'purchase' : 'purchases'
                    } booked in an earlier month (${carried.fromPeriods
                      .map((p) => `${p.count} from ${p.period}`)
                      .join(', ')}) that matched this month's 2B${
                      carried.amounts.length > 0
                        ? ` — ${carried.amounts
                            .map((a) => `${HEAD_LABELS[a.head]} ${rupees(a.paise)}`)
                            .join(', ')}`
                        : ''
                    }.`}
                  </Alert>
                )
              })()}
              <FormBlock
                head="Details"
                rows={ITC_ROWS}
                cells={cells}
                columns={HEAD_COLUMNS}
                alwaysSay
              />
              <ListedInputs cell={cells['4B(1)']} />
              {selected && can(PERIOD, 'can_update') && (
                <Gstr3bTypedFigure periodId={selected} onSaved={load} />
              )}

              {/* -- 5 ------------------------------------------------------ */}
              <FormBlock
                title="5. Values of exempt, nil-rated and non-GST inward supplies"
                head="Nature of supplies"
                rows={INWARD_EXEMPT_ROWS}
                cells={cells}
                columns={[
                  { key: 'inter_state', label: 'Inter-State supplies' },
                  { key: 'intra_state', label: 'Intra-State supplies' },
                ]}
                alwaysSay
              />

              {/* -- 5.1 ---------------------------------------------------- */}
              <FormBlock
                title="5.1 Interest and late fee"
                head="Description"
                rows={INTEREST_ROWS}
                cells={cells}
                columns={HEAD_COLUMNS}
                alwaysSay
              />

              {/* -- 6.1 ---------------------------------------------------- */}
              <h3 className="gst-sub mb-2">6.1 Payment of tax</h3>
              {/* R-E5: the portal's ledger download comes first (D-97); with none, a balance a
                  person types here is used and named. Never 0.00 for a balance nobody gave. */}
              {selected && (
                <Gstr3bLedgers
                  periodId={selected}
                  ledgers={body.ledgers}
                  canType={can(PERIOD, 'can_update')}
                  onSaved={load}
                />
              )}
              {/* W3-18 (1): with the credit not established, 6.1 carries a state and
                  no figures. Until 2026-10-14 this block rendered the set-off anyway,
                  under a warning, and the working paper printed it with none. */}
              {!payment.established ? (
                <Alert tone="warning" className="mb-5">
                  {payment.says}
                </Alert>
              ) : !setoff ? (
                <div className="gst-empty">No set-off computed for this period.</div>
              ) : (
                <div className="gst-table-wrap mb-5">
                  <table className="gst-table">
                    <thead>
                      <tr>
                        <th className="is-text">Description</th>
                        {HEADS.map((h) => (
                          <th key={h}>{HEAD_LABELS[h]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="is-text is-sec">Paid through ITC</td>
                        {HEADS.map((h) => (
                          <td key={h}>
                            {rupees(
                              Object.values(setoff.utilisation ?? {}).reduce(
                                (acc, byHead) => acc + (byHead?.[h] ?? 0),
                                0,
                              ),
                            )}
                          </td>
                        ))}
                      </tr>
                      <tr className="is-a">
                        <td className="is-text is-sec">Paid in cash</td>
                        {HEADS.map((h) => (
                          <td key={h}>{rupees(setoff.cash?.[h])}</td>
                        ))}
                      </tr>
                      {/* W3-18 (8), D-97: the cash ledger's balance pays the cash first. */}
                      {payment.deposit && (
                        <>
                          <tr>
                            <td className="is-text is-sec">Paid from the cash ledger's balance</td>
                            {HEADS.map((h) => (
                              <td key={h}>{rupees(payment.deposit?.fromBalance[h])}</td>
                            ))}
                          </tr>
                          <tr className="is-a">
                            <td className="is-text is-sec">Cash to deposit</td>
                            {HEADS.map((h) => (
                              <td key={h}>{rupees(payment.deposit?.toDeposit[h])}</td>
                            ))}
                          </tr>
                        </>
                      )}
                      <tr className="is-net">
                        <td className="is-text is-sec">Credit carried forward</td>
                        {HEADS.map((h) => (
                          <td key={h}>{rupees(setoff.credit_remaining?.[h])}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Said under the figures they rest on (W3-18 (1)): until the portal's
                  ledger download is read (D-97), no credit is brought forward. */}
              {/* W3-18 (8): which portal balances 6.1 opened with, as on when — or that the
                  cash ledger's was not supplied, so the deposit is not worked out. */}
              {payment.established && !payment.noOpeningCredit && payment.says && (
                <p className="gst-meta mb-5">{payment.says}</p>
              )}
              {payment.established && payment.noOpeningCredit && (
                <p className="gst-meta mb-5">
                  No balance from the electronic credit ledger was supplied, so no credit is
                  brought forward from earlier months. The cash shown may be more than is due.
                </p>
              )}
              {/* -- the challan (C-08, FR-E23) ------------------------------- */}
              {view?.challan && (
                <div className="mb-5">
                  <p className="gst-panel-h mb-1">
                    Challan — cash to deposit, by head
                    {view.challan.complete ? (
                      <span className="gst-tag is-clear">₹{rupees(view.challan.total)}</span>
                    ) : (
                      <span className="gst-tag">₹{rupees(view.challan.total)} · not all heads</span>
                    )}
                  </p>
                  <p className="gst-meta mb-2">{view.challan.sentence}</p>
                  <div className="gst-table-wrap">
                    <table className="gst-table">
                      <thead>
                        <tr>
                          <th className="is-text">Major head</th>
                          {MINOR_HEADS.map(([key, words]) => (
                            <th key={key}>{words}</th>
                          ))}
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {challanRows(view.challan).map((row) => (
                          <tr key={row.head}>
                            <td className="is-text is-sec">{row.head}</td>
                            {MINOR_HEADS.map(([key]) => (
                              <td key={key}>
                                {row.amounts[key] === null ? (
                                  <span className="gst-chip is-hold">not computed</span>
                                ) : (
                                  rupees(row.amounts[key])
                                )}
                              </td>
                            ))}
                            <td>{rupees(row.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {challanLeftOut(view.challan).map((line) => (
                    <p key={line} className="gst-meta mt-1">
                      {line}
                    </p>
                  ))}
                  <p className="gst-meta mt-1">Electronic cash ledger: {view.challan.cash_ledger}.</p>
                  {can(PERIOD, 'can_update') && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      disabled={busy}
                      onClick={() => void downloadChallan()}
                    >
                      <FileSpreadsheet size={14} /> Download the challan working
                    </Button>
                  )}
                </div>
              )}

              {/* FR-E16 (B-10): a quarterly 3B's first two months are paid by PMT-06 deposits,
                  which the quarter's challan above does not show. Returns had the card; the 3B
                  screen, where the quarter's payment is read, did not until C-14. */}
              {body.period.return_type === 'GSTR3B' && body.period.period_type === 'quarterly' && (
                <div className="mb-5">
                  <Pmt06Card
                    key={`pmt06:${body.period.id}`}
                    clientId={body.period.client_id}
                    quarter={body.period.period}
                  />
                </div>
              )}

              {/* -- the GSTR-3B JSON (C-09, FR-E24) -------------------------- */}
              {view && (
                <Alert tone={jsonHolds(view).length ? 'warning' : 'success'} className="mb-5">
                  <p className="gst-panel-h">
                    GSTR-3B JSON
                    <span className={`gst-tag ${jsonHolds(view).length ? '' : 'is-clear'}`}>
                      {jsonHolds(view).length
                        ? `held · ${jsonHolds(view).length} cell${jsonHolds(view).length === 1 ? '' : 's'}`
                        : 'every cell has a figure'}
                    </span>
                  </p>
                  <p className="text-sm">
                    Written when the client approves, from the figures above in whole rupees. A
                    cell with no figure holds it until a figure is there.
                  </p>
                  {jsonHolds(view).length > 0 && (
                    <ul className="gst-panel-list">
                      {jsonHolds(view).map((h) => (
                        <li key={h.cell}>
                          <span className="gst-k">{h.cell}</span>
                          <span>{h.why}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {gates &&
                    gates.period.id === selected &&
                    ['client_approved', 'filed', 'locked'].includes(gates.period.state) &&
                    can(PERIOD, 'can_update') && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        disabled={busy}
                        onClick={() => void makeJson()}
                      >
                        Make the GSTR-3B JSON again
                      </Button>
                    )}
                </Alert>
              )}

              {/* -- GSTN's auto-calculated figures, beside ours (D-10, FR-E35) -- */}
              {selected && gates?.period.id === selected && (
                <Gstr3bAutocalc
                  key={autocalcRead}
                  periodId={selected}
                  periodState={gates.period.state}
                  mayUpdate={can(PERIOD, 'can_update')}
                />
              )}

              {/* D-37 — the per-head net, BESIDE 88A and visibly not part of the
                  return. Its own table and its own heading, because a row
                  inside the 6.1 table would read as one more figure to file. */}
              {netView && (
                <div className="mb-5">
                  <p className="gst-panel-h mb-1">
                    Per-head net — for tying to your working, not filed
                    {netView.differences.length > 0 ? (
                      <span className="gst-tag">differs from 6.1</span>
                    ) : (
                      <span className="gst-tag is-clear">same as 6.1</span>
                    )}
                  </p>
                  <p className="gst-meta mb-2">
                    {netView.note} Shown in whole rupees, like 6.1 above; the working paper
                    carries it to the paisa.
                  </p>
                  <div className="gst-table-wrap">
                    <table className="gst-table">
                      <thead>
                        <tr>
                          <th className="is-text">Each head on its own</th>
                          {HEADS.map((h) => (
                            <th key={h}>{HEAD_LABELS[h]}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="is-text is-sec">Cash, per head</td>
                          {HEADS.map((h) => (
                            <td key={h}>{rupees(netView.cash[h])}</td>
                          ))}
                        </tr>
                        <tr>
                          <td className="is-text is-sec">Credit carried forward, per head</td>
                          {HEADS.map((h) => (
                            <td key={h}>{rupees(netView.creditRemaining[h])}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {netView.differences.length > 0 && (
                    <p className="gst-meta mt-2">
                      A working that nets each head shows more cash than 6.1 by{' '}
                      {netView.differences
                        .map((d) => `₹${rupees(d.paise)} ${HEAD_LABELS[d.head]}`)
                        .join(', ')}{' '}
                      — credit Rule 88A lets another head use.
                    </p>
                  )}
                </div>
              )}

              {/* -- tie-out ------------------------------------------------ */}
              <Alert tone={body.tieout.ok ? 'success' : 'warning'}>
                <p className="gst-panel-h">
                  Tie-out with GSTR-1
                  <span className={`gst-tag ${body.tieout.ok ? 'is-clear' : ''}`}>
                    {body.tieout.ok ? 'reconciles' : `${body.tieout.discrepancies.length} to review`}
                  </span>
                </p>
                {body.tieout.ok ? (
                  <p className="text-sm">GSTR-1 and GSTR-3B outward figures reconcile.</p>
                ) : (
                  <ul className="gst-panel-list">
                    {body.tieout.discrepancies.map((d, i) => (
                      <li key={i}>
                        <span className="gst-k">3.1</span>
                        <span>{String(d.message ?? JSON.stringify(d))}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Alert>
            </>
          )}
        </section>
      </div>
    </div>
  )
}
